import { useCallback, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Check, GraduationCap } from 'lucide-react-native';
import { AppButton } from '../components/ui/AppButton';
import { AppCard } from '../components/ui/AppCard';
import { AppText } from '../components/ui/AppText';
import { showAppToast } from '../components/ui/AppToast';
import { tokens } from '../theme/tokens';
import { completePrototypeOnboarding } from '../utils/prototypeSession';
import type { GradeOption } from '../types/prototypeSession';

const gradeOptions: GradeOption[] = [
  { id: 'grade-7', label: '初一', description: '适合刚开始建立知识网络的同学' },
  { id: 'grade-8', label: '初二', description: '重点关注几何、函数和物理入门' },
  { id: 'grade-9', label: '初三', description: '围绕中考复习、错题和试卷分析' },
  { id: 'grade-10', label: '高一', description: '衔接高中数学、物理和英语能力' },
];

export default function GradeSelectPage() {
  const router = useRouter();
  const [selectedGradeId, setSelectedGradeId] = useState('grade-9');
  const [saving, setSaving] = useState(false);

  const selectedGrade =
    gradeOptions.find((grade) => grade.id === selectedGradeId) ?? gradeOptions[0];

  const finishOnboarding = useCallback(async () => {
    if (!selectedGrade || saving) {
      return;
    }

    setSaving(true);
    try {
      await completePrototypeOnboarding(selectedGrade.label);
      showAppToast(`已选择${selectedGrade.label}`, 'success');
      router.replace('/');
    } finally {
      setSaving(false);
    }
  }, [router, saving, selectedGrade]);

  return (
    <SafeAreaView style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <View style={styles.heroIcon}>
            <GraduationCap size={30} color={tokens.colors.white} />
          </View>
          <AppText variant="title" weight="bold" align="center">
            选择当前年级
          </AppText>
          <AppText variant="body" tone="muted" align="center">
            原型阶段先用本地年级标签驱动首页和学习画像，后续再接真实学生档案。
          </AppText>
        </View>

        <View style={styles.gradeStack}>
          {gradeOptions.map((grade) => {
            const selected = grade.id === selectedGradeId;
            return (
              <Pressable
                key={grade.id}
                accessibilityRole="button"
                onPress={() => setSelectedGradeId(grade.id)}
                style={({ pressed }) => [pressed ? styles.pressed : null]}
              >
                <AppCard style={[styles.gradeCard, selected ? styles.gradeCardSelected : null]}>
                  <View style={styles.gradeCopy}>
                    <AppText variant="subtitle" weight="bold">
                      {grade.label}
                    </AppText>
                    <AppText variant="caption" tone="muted">
                      {grade.description}
                    </AppText>
                  </View>
                  <View style={[styles.checkCircle, selected ? styles.checkCircleSelected : null]}>
                    {selected ? <Check size={16} color={tokens.colors.white} /> : null}
                  </View>
                </AppCard>
              </Pressable>
            );
          })}
        </View>

        <AppButton label="进入登峰" loading={saving} onPress={finishOnboarding} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.xxl,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.xl,
  },
  hero: {
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  heroIcon: {
    width: 66,
    height: 66,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.primary,
  },
  gradeStack: {
    gap: tokens.spacing.md,
  },
  gradeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  gradeCardSelected: {
    borderColor: tokens.colors.primary,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  gradeCopy: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  checkCircle: {
    width: 28,
    height: 28,
    borderRadius: tokens.radius.full,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surface,
  },
  checkCircleSelected: {
    borderColor: tokens.colors.primary,
    backgroundColor: tokens.colors.primary,
  },
  pressed: {
    opacity: 0.86,
  },
});
